import type BackOfficeConnector from "data/backOfficeConnector";
import PayGateToTransferRefundDetailResponse from "entities/payGateToTransferRefundDetailResponse";
import PayGateToTransferRefundsResponse from "entities/payGateToTransferRefundsResponse";
import type RefundTransferPayment from "entities/refundTransferPaymentResponse";
import TransferRefundDetailResponse from "entities/transferRefundDetailResponse";
import type EventBus from "services/eventBus";
import TransferRefundResponse from "entities/transferRefundResponse";
import RepositoryBase from "./repositoryBase";
import type { IPagination } from "data/types";
import PayGateRefundResponse from "entities/payGateRefundResponse";
import { excludeEmptyValues, formatAccountValues, serializeFilters } from "utils/filters";
import type PayGateRefundsSearchFilter from "entities/filters/refunds/payGateRefundsSearchFilter";
import DistributorRefundResponse from "entities/distributorRefundResponse";
import DistributorRefundDetailResponse from "entities/distributorRefundDetailResponse";
import type { IApprovalRepository } from "./types";
import type TransferRefundsRequest from "entities/transferRefundsRequest";
import type PayGateRefundsRequest from "entities/payGateRefundsRequest";
import PayGateRefundDetailResponse from "entities/payGateRefundDetailResponse";
import type PayGateToTransferRefunds from "entities/payGateToTransferRefundsRequest";
import type RefundsApprovalsRequest from "entities/refundsApprovalsRequest";

export default class RefundRepository extends RepositoryBase<BackOfficeConnector> implements IApprovalRepository {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getTransferRefunds(pagination: IPagination, filter: TransferRefundsRequest) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("transfer_refund_search")
          .postPagedEntities(
            {
              ...serializeFilters(
                {
                  ...filter,
                  bankAccount: { accountNumber: filter.senderBankAccount?.accountNumber, bank: filter.senderBankAccount?.bank },
                },
                false
              ),
              ...pagination,
            },
            TransferRefundResponse
          ),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  getTransferRefund(id: string) {
    return this.callApi(api => api.all("finance").one("transfer_refunds", id).getEntity(TransferRefundDetailResponse), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
    });
  }

  updateTransferRefundPayment(refundId: string, refundPayment: RefundTransferPayment) {
    const { counterpartyBank, ...rest } = refundPayment;
    const request = { counterPartyBank: formatAccountValues(counterpartyBank), ...rest };

    if (request?.counterPartyBank?.bankAccount && !request?.counterPartyBank?.bankAccount.bank) {
      request.counterPartyBank.bankAccount.bank = undefined;
    }

    return this.callApi(
      api => api.all("finance").one("transfer_refunds", refundId).all("refund_payment").putOnly(excludeEmptyValues(request)),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.3" }),
      }
    );
  }

  getAllPayGateRefunds(pagination: IPagination, filter?: PayGateRefundsRequest) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("paygate_refund_search")
          .postPagedEntities<PayGateRefundResponse>(
            { ...(filter ? serializeFilters(filter, false) : {}), ...pagination },
            PayGateRefundResponse
          ),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  getPayGateRefund(id: string) {
    return this.callApi(api => api.all("finance").one("paygate_refunds", id).getEntity(PayGateRefundDetailResponse), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
    });
  }

  getAllDistributorRefunds(pagination: IPagination, filter?: PayGateRefundsSearchFilter) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("distributor_refund_search")
        .postPagedEntities<DistributorRefundResponse>(
          { ...(filter ? excludeEmptyValues(filter) : {}), ...pagination },
          DistributorRefundResponse
        )
    );
  }

  getDistributorRefund(id: string) {
    return this.callApi(api => api.all("finance").one("distributor_refunds", id).getEntity(DistributorRefundDetailResponse));
  }

  getAllPayGateToTransferRefund(pagination: IPagination, filter?: PayGateToTransferRefunds) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("paygate_to_transfer_refund_search")
          .postEntity<PayGateToTransferRefundsResponse>(
            { ...(filter ? serializeFilters(filter, false) : {}), ...pagination },
            PayGateToTransferRefundsResponse
          ),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  getPayGateToTransferRefund(id: string) {
    return this.callApi(
      api => api.all("finance").one("paygate_to_transfer_refunds", id).getEntity(PayGateToTransferRefundDetailResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.5" }),
      }
    );
  }

  updatePayGateToTransferRefundPayment(refundId: string, refundPayment: RefundTransferPayment) {
    const { counterpartyBank, ...rest } = refundPayment;
    const request = { counterPartyBank: formatAccountValues(counterpartyBank), ...rest };
    if (request?.counterPartyBank?.bankAccount && !request?.counterPartyBank?.bankAccount.bank) {
      request.counterPartyBank.bankAccount.bank = undefined;
    }
    return this.callApi(api =>
      api.all("finance").one("paygate_to_transfer_refunds", refundId).all("refund_payment").putOnly(excludeEmptyValues(request))
    );
  }

  approve(id: string) {
    return this.callApi(api => api.all("finance").one("refunds", id).all("approvals").postWithoutResponse({}));
  }

  approveRefunds(request: RefundsApprovalsRequest) {
    return this.callApi(api => api.all("finance").all("refunds").all("approvals").postOnly(request), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }

  exportApprovedRefunds() {
    return this.callApi(api => api.all("finance").all("refunds").all("exports").postWithoutResponse({}));
  }
}
